// ** React Imports
import { useContext } from "react";

// ** Third Party Components
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import loadable from "@loadable/component";

// ** Custom Components
import Block from "components/layout/Block";
import Paragraph from "components/typography/Paragraph";
import Link from "components/layout/Link";
import Avatar from "./Avatar";
import Name from "../user/Name";

const FollowersButton = loadable(
	() => import("containers/Users/components/FollowersButton"),
	{
		ssr: false,
	}
);

const FollowersButtonNotAuth = loadable(
	() => import("containers/Users/components/FollowersButtonNotAuth"),
	{
		ssr: false,
	}
);

// ** Contexts
import deviceTypeContext from "contexts/deviceTypeContext";
import authContext from "contexts/authContext";

// ** Styles Import

import { breakpoints } from "styles/grid";

// #####################################################

const LinkStyled = styled(Link)`
	width: 100%;
	height: 100%;

	border-radius: ${({ theme: { borderRadius } }) => borderRadius.panel};
	background-color: transparent;

	color: ${({ theme: { colors } }) => colors.black};
	&:hover,
	&:focus {
		background-color: ${({ theme: { colors } }) => colors.itemsHover};
	}

	${({ $usersListingOldWithNewProfile }) =>
		$usersListingOldWithNewProfile &&
		css`
			padding: 1rem;
			&:hover,
			&:focus {
				@media (max-width: ${breakpoints.phone - 1}px) {
					border-radius: 0;
				}

				background-color: ${({ theme: { colors } }) =>
					colors.itemsHover};
			}
		`}
`;
LinkStyled.displayName = "TileLink";

// #####################################################

const Media = ({
	to,
	avatarComponent,
	avatarProps,
	avatarAlt,
	followers,
	name,
	subname,
	description,
	padding = 2,
	paddingY = 2,
	centerContent,
	mediaStyles,
	smallOnMobile,
	scrollForce,
	noXmargins, // TODO: usunąć po zakończeniu badań
}) => {
	const { isPhone } = useContext(deviceTypeContext);

	const isAuth = useContext(authContext);

	const AvatarComponent = avatarComponent;

	const finalAvatarProps = {
		size: isPhone && smallOnMobile ? "small" : "medium",
		...avatarProps,
	};
	const renderName = () => {
		if (typeof name === "function") {
			return name();
		}
		return <Name content={name} />;
	};
	const renderSubname = () => {
		if (typeof subname === "function") {
			return subname();
		}
		return <Paragraph>{subname}</Paragraph>;
	};
	const renderDescription = () => {
		if (typeof description === "function") {
			return description();
		}
		return (
			<Paragraph mb={0} textBreak>
				{description}
			</Paragraph>
		);
	};

	const renderContent = () => (
		<Block flex p={padding} py={paddingY} style={{ minWidth: "0" }}>
			<Block
				relative
				flex
				alignCenter
				mx={noXmargins ? 0 : 2}
				mr={noXmargins ? 2 : 0}
			>
				<AvatarComponent {...finalAvatarProps} alt={avatarAlt} />
			</Block>
			<Block
				fullWidth
				style={{ minWidth: "0" }}
				flex
				column
				py={1}
				px={2}
				justifyStart={!centerContent}
				justifyCenter={centerContent}
				color="black"
				{...mediaStyles}
			>
				{name ? renderName() : null}
				{subname ? renderSubname() : null}
				{description ? renderDescription() : null}
			</Block>
		</Block>
	);

	// #####################################################

	if (to) {
		return (
			<>
				<LinkStyled
					scrollForce={scrollForce}
					to={to}
					$usersListingOldWithNewProfile={noXmargins}
				>
					{renderContent()}
				</LinkStyled>
				{followers?.is_allowed && isAuth && (
					<FollowersButton followers={followers} />
				)}
				{!isAuth && <FollowersButtonNotAuth />}
			</>
		);
	}
	return renderContent();
};

// #####################################################

Media.displayName = "Media";

Media.defaultProps = {
	to: false,
	avatarComponent: Avatar,
	smallOnMobile: true,
};

Media.propTypes = {
	to: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
	avatarComponent: PropTypes.elementType,
	smallOnMobile: PropTypes.bool, // show small avatar on mobile
};

export default Media;

// #####################################################
